import React, { useContext, useEffect, useState } from 'react';
import { QuestionTask } from '../../constants/AllTypes';
import Taskbar from '../Taskbar';
import { OpenEndedStyles as styles } from '../../css/styles';
import { correctIcon, incorrectIcon } from '../../constants/icons';
import { LessonContext } from '../../context/LessonContext';
import classNames from 'classnames';
import { fetchImage } from '../../utils/functions';
import { AuthContext } from '../../context/AuthContext';
import { useLessonData } from '../../hooks/useLessonData';
import { useTestTasks } from '../../hooks/useTestTasks';
import { useSendHomework } from '../../hooks/useSendHomework';
import { useTestProgress } from '../../hooks/useTestProgress';
import CoinAnimation from '../CoinAnimation';

type OpenEndedProps = {
    task: QuestionTask;
    isFullscreen: boolean;
}

type SubmitData = {
  studentAnswers: string[];
}

const OpenEnded: React.FC<OpenEndedProps> = ({ task, isFullscreen }) => {
    const [questions, setQuestions] = useState<{ question: string; image?: string; answer: string }[]>([{ question: '', image: '', answer: '' }]);
    const [revealedCount, setRevealedCount] = useState(0);
    const [studentAnswers, setStudentAnswers] = useState<string[]>([]);
    const [results, setResults] = useState<string[]>([]);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const taskId = Number(task?.taskId)
    const { checkingResult, currentLesson, currentTaskIndex, studentId, currentTestAttempt} = useContext(LessonContext);
    const { currentUser } = useContext(AuthContext);
    const [fetchedImages, setFetchedImages] = useState<{ [key: string]: string | null }>({});
    const { sendHomework, coinAnimationData } = useSendHomework();
    const [coinAnimationFinished, setCoinAnimationFinished] = useState(false);
    const [coinAnimationReady, setCoinAnimationReady] = useState(false);

    const {data: lessonData} = useLessonData(Number(currentLesson?.lessonId))
    const lessonPlanParts = lessonData?.lessonPlanParts || []

    const { data: testTasks } = useTestTasks(Number(currentLesson?.lessonId), Number(studentId), currentTestAttempt, checkingResult);
    const { testProgress, updateTaskProgress } = useTestProgress(Boolean(currentLesson?.isTest), Number(currentLesson?.lessonId), currentTestAttempt);

    useEffect(() => {
        if (task?.taskContent?.questions) {
          setQuestions(task.taskContent.questions);
        }
        if (checkingResult) {
            if (task.submit) {
              // setMatchedPairs(JSON.parse(task.submit));
              const progressData = typeof task.submit === 'string' ? JSON.parse(task.submit) : task.submit
              setStudentAnswers(progressData.studentAnswers || []);
    
          //test results
          } else if (testTasks) {
              const testTask = testTasks.find((t) => t.taskId === Number(task.taskId));
              // submittedAnswers = testTasks.find(testTask => testTask.taskId === taskId)?.submit as Pairs[] ?? null;
              if (testTask?.submit === null) {
                  return
              }
              let parsedSubmit = testTask?.submit as SubmitData || { studentAnswers: []};
              setStudentAnswers(parsedSubmit.studentAnswers || [])
          } 
          setIsSubmitted(true)
          
        }
        //test progress
        if (currentLesson?.isTest && testProgress && testProgress?.[Number(task.taskId)] && testProgress?.[Number(task.taskId)].submit !== null) {
          const progressData = testProgress[Number(task.taskId)].submit;
          setStudentAnswers(progressData.studentAnswers || []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [task]);

    useEffect(() => {
        const fetchAllImages = async () => {
            if (!questions) return;

            const newFetchedImages = { ...fetchedImages };
            for (const question of questions) {
                if (question.image && !newFetchedImages[question.image]) {
                    const imageUrl = await fetchImage(question.image);
                    newFetchedImages[question.image] = imageUrl;
                }
            }
            setFetchedImages(newFetchedImages);
        };

        fetchAllImages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [questions]);


    const showAnswer = () => {
        if (revealedCount < questions.length) {
          setRevealedCount(revealedCount + 1);
        }
    };

    const showAllAnswers = () => {
        if (questions) {
          setRevealedCount(questions.length);
        }
      };

      const handleClear = () => {
        setRevealedCount(0);
        setStudentAnswers([]);
        setIsSubmitted(false);
      };

      const handleStudentAnswerChange = (index: number, value: string) => {
        setStudentAnswers(prev => {
          const updated = [...prev];
          updated[index] = value;
          return updated;
        });
      };

      const checkAnswer = (studentAnswer: string, correctAnswer: string): string => {
        const normalized = studentAnswer.toLowerCase();
        const correct = correctAnswer.toLowerCase();
      
        // Must include the correct answer
        if (!normalized.includes(correct)) return "incorrect";
      
        // Negation detection near the answer
        const negationRegex = new RegExp(`\\b(not|isn't|no|never)\\b\\s+${correct}`);
        if (negationRegex.test(normalized)) return "incorrect";
      
        return "correct";
      };

      const handleSubmit = () => {
        if (!studentAnswers) return;
      
        const newResults = questions.map((q, index) => {
          const studentAns = studentAnswers[index] || "";
          return checkAnswer(studentAns, q.answer);
        });
      
        setIsSubmitted(true);
      
        const correctCount = newResults.filter(result => result === "correct").length;
        const totalQuestions = questions.length;
      
        const percentage = totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100).toFixed(0) : 0;
        const percentageCorrect = `${percentage}%`
      
        const submit = { studentAnswers }
        sendHomework(percentageCorrect, task, submit, currentUser)
        setTimeout(() => {
          setCoinAnimationReady(true)
        }, 1000); 
        if (task.isHomework && currentUser?.mode === 'student') {
          localStorage.removeItem(`homework_${taskId}`);
        }
      };

      useEffect(()=> {
        if (isSubmitted) {
          const newResults = questions.map((q, index) => {
            const studentAns = studentAnswers[index] || "";
            return checkAnswer(studentAns, q.answer);
          });
          
          setResults(newResults);
        }
      // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [isSubmitted])

    // TEST LOGIC

  // saving progress
  useEffect(() => {
    if (currentLesson?.isTest && lessonPlanParts[currentTaskIndex].taskId === task.taskId && !checkingResult) {
        const newResults = questions.map((q, index) => {
          const studentAns = studentAnswers[index] || "";
          return checkAnswer(studentAns, q.answer);
        });

        const correctMarks = newResults.filter(result => result === "correct").length;
        const possibleMarks = questions.length;
        const submit = { studentAnswers }
        // const submit = matchedPairs;
        const taskTitle = task.taskTitle;
        const skillCategory = task.skillCategory
        const taskOrder = task.taskOrder
        const taskType = task.taskType

        if (updateTaskProgress && taskTitle) {
            // updateTaskProgress(taskId, { submit, correctMarks, possibleMarks, taskTitle, taskInfo: {bGroup}});
            updateTaskProgress(taskId, { submit, correctMarks, possibleMarks, taskTitle, taskType, skillCategory, taskOrder });
        }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentAnswers])

  //Restore Homework data
  useEffect(() => {
    const saved = localStorage.getItem(`homework_${taskId}`);
    if (!saved || checkingResult) return;
    try {
      const parsed = JSON.parse(saved) as string[];
      setStudentAnswers(parsed);
    } catch (err) {
      console.error('Invalid saved homework data', err);
      localStorage.removeItem(`homework_${taskId}`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  //RESTORE LOST HOMEWORK
  useEffect(() => {
    if (task.isHomework && currentUser?.mode === 'student' && !isSubmitted && !checkingResult) {
      localStorage.setItem(`homework_${taskId}`, JSON.stringify(studentAnswers));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentAnswers]);

    return (
        <div className={classNames(styles.container, { [styles.fullscreen] : isFullscreen })}>
          {questions.map((q, index) => (
            <div key={index} className={styles.item}>
              <div className={styles.question}>
                <p className="question-number">{index + 1}</p>
                <p> {q.question}</p>
              </div>
              {q.image && fetchedImages[q.image] &&  (
                <img src={String(fetchedImages[q.image])} alt={`Question ${index + 1}`} className={styles.image}/>
              )} 
              <div className={styles.inputContainer}>
                  <textarea
                      className={styles.studentAnswer}
                      value={studentAnswers[index] || ""}
                      onChange={(e) => {
                        const cleaned = e.target.value.replace(/\s{2,}/g, ' ');
                        handleStudentAnswerChange(index, cleaned)
                      }}
                      name="answer"
                  />
                    {isSubmitted && (
                      <>
                          <img
                              src={results[index] === "correct" ? correctIcon : incorrectIcon}
                              alt={results[index]}
                              className={styles.resultIcon}
                          />
                          {results[index] === "incorrect" && (
                              <p className='correct-answer'>
                                  {q.answer}
                              </p>
                          )}
                      </>
                  )}
              </div>
              <div>
                {index < revealedCount ? (
                  <p className={styles.answer}>{q.answer}</p>
                ) : (
                  <p className={styles.hiddenAnswer}></p>
                )}
              </div>
            </div>
          ))}
            <Taskbar
              isFullscreen={isFullscreen}
              handleShowAnswer={showAllAnswers}
              handleShowSingleAnswer={showAnswer}
              handleClear={handleClear}
              handleSubmit={handleSubmit}  
              isSubmitted={isSubmitted}
            />
            {(coinAnimationData && coinAnimationData.newCoins && coinAnimationReady && !coinAnimationFinished) ? (
              <div className="coin-overlay">
                <CoinAnimation
                  currentCoins={coinAnimationData.currentCoins}
                  coinsToAward={coinAnimationData.newCoins}
                  setCoinAnimationFinished={setCoinAnimationFinished}
                />
              </div>
            ) : null}
        </div>
      );
};

export default OpenEnded;